import React, { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2, Loader2, AlertCircle, Building2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { COMPANY, SOCIALS, addressLines, addressInline } from "@/data/company";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import { trackLeadGenerated, trackPhoneClick } from "@/lib/track";
import { getAttributionPayload } from "@/lib/attribution";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  message: "",
  website: "", // honeypot — must always stay empty on submit
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const payload = {
        ...form,
        phone: form.phone || null,
        company: form.company || null,
        service: form.service || null,
        ...getAttributionPayload(),
      };
      await submitContact(payload);
      trackLeadGenerated({
        service: payload.service,
        company: payload.company,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const fallback =
        status === 429
          ? "You've sent a few messages recently — please try again in a few minutes, or call us on +44 7599 474730 if it's urgent."
          : "Unable to send your message right now. Please try again or call us.";
      setError(
        Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(", ")
          : (typeof detail === "string" && detail) || fallback
      );
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Talk to a Lutrom engineer. 24/7 call-out for industrial and commercial clients across North West England. Call +44 7599 474730 or send an enquiry."
        path="/contact"
      />
      <section className="relative overflow-hidden">
        <PhotoBackdrop src="/gallery/workshop.jpg" position="center 35%" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-12">
          <p className="eyebrow">Contact</p>
          <h1
            className="font-display mt-6 text-white"
            style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 0.95 }}
            data-testid="contact-heading"
          >
            TALK TO A
            <br />
            <span className="gradient-blue-text">LUTROM ENGINEER.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[color:var(--lt-text-dim)]">
            Tell us about your site, asset or issue — we&apos;ll come back with a
            recommendation and indicative scope. Urgent? Call our line direct.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-4 space-y-5">
          {[
            {
              icon: Phone,
              label: "24/7 Call-out",
              value: COMPANY.phone.display,
              href: COMPANY.phone.tel,
              testid: "contact-info-phone",
            },
            {
              icon: Mail,
              label: "Email enquiries",
              value: COMPANY.email.display,
              href: COMPANY.email.mailto,
              testid: "contact-info-email",
            },
            {
              icon: Building2,
              label: "Head office",
              value: COMPANY.name,
              extra: addressLines(),
              href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressInline())}`,
              external: true,
              testid: "contact-info-location",
            },
          ].map((item) => {
            const Icon = item.icon;
            const Wrapper = item.href ? "a" : "div";
            const extraProps = item.href
              ? item.external
                ? { href: item.href, target: "_blank", rel: "noreferrer" }
                : { href: item.href }
              : {};
            const onClick = item.testid === "contact-info-phone"
              ? () => trackPhoneClick("contact_sidebar")
              : undefined;
            return (
              <Wrapper
                key={item.label}
                {...extraProps}
                onClick={onClick}
                className="glass-card rounded-xl p-6 flex items-start gap-4 hover:border-[color:var(--lt-blue-soft)] transition-colors block"
                data-testid={item.testid}
              >
                <span className="icon-tile flex-shrink-0" style={{ width: 44, height: 44 }}>
                  <Icon size={20} />
                </span>
                <div className="min-w-0">
                  <p className="eyebrow" style={{ margin: 0 }}>{item.label}</p>
                  <p className="mt-2 text-white font-semibold break-words">{item.value}</p>
                  {item.extra && (
                    <div className="mt-1 text-sm text-[color:var(--lt-text-dim)] leading-relaxed">
                      {item.extra.map((l) => (
                        <span key={l} className="block">{l}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Wrapper>
            );
          })}

          <div
            className="glass-card rounded-xl p-6"
            data-testid="contact-socials-card"
          >
            <p className="eyebrow" style={{ margin: 0 }}>Follow Lutrom</p>
            <ul className="mt-4 grid grid-cols-3 gap-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.name}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.name}
                      title={`${s.name} — ${s.handle}`}
                      className="flex flex-col items-center justify-center gap-2 rounded-md py-3 transition-colors"
                      style={{
                        background: "rgba(6, 11, 24, 0.55)",
                        border: "1px solid var(--lt-border-strong)",
                        color: "var(--lt-text)",
                      }}
                      data-testid={`contact-social-${s.name.toLowerCase()}`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "white";
                        e.currentTarget.style.borderColor = "rgba(127, 166, 255, 0.7)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "var(--lt-text)";
                        e.currentTarget.style.borderColor = "var(--lt-border-strong)";
                      }}
                    >
                      <Icon size={18} />
                      <span className="text-[11px] uppercase tracking-widest font-semibold">
                        {s.name}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="glow-card rounded-xl p-6">
            <p className="eyebrow" style={{ color: "rgba(230,236,255,0.85)" }}>Existing client?</p>
            <p className="mt-3 text-white text-sm leading-relaxed">
              Submit work requests, view reports and track jobs in the Lutrom
              Operations Portal.
            </p>
            <a
              href={COMPANY.portal}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white"
              data-testid="contact-portal-link"
            >
              Open the Portal <ArrowRight size={14} />
            </a>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <form
            onSubmit={onSubmit}
            className="glass-card rounded-2xl p-7 lg:p-10 space-y-5"
            data-testid="contact-form"
            noValidate
          >
            {/* Honeypot — hidden from humans, catnip for scraper bots.
                Legitimate users never see or interact with this field. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="lutrom-website-hp">
                Website (leave this field empty)
              </label>
              <input
                id="lutrom-website-hp"
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                data-testid="contact-field-honeypot"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Full name"
                required
                testid="contact-field-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="Jane Smith"
              />
              <Field
                label="Email"
                type="email"
                required
                testid="contact-field-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="jane@example.com"
              />
              <Field
                label="Phone"
                testid="contact-field-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="+44 ..."
              />
              <Field
                label="Company"
                testid="contact-field-company"
                value={form.company}
                onChange={(v) => update("company", v)}
                placeholder="Your business"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[color:var(--lt-text-dim)] mb-2">
                Service of interest
              </label>
              <select
                value={form.service}
                onChange={(e) => update("service", e.target.value)}
                className="field w-full rounded-md px-4 py-3 text-sm"
                data-testid="contact-field-service"
              >
                <option value="">Select a service (optional)</option>
                {SERVICES.map((s) => (
                  <option key={s.slug} value={s.title}>
                    {s.title}
                  </option>
                ))}
                <option value="Other">Other / not sure</option>
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[color:var(--lt-text-dim)] mb-2">
                Message <span style={{ color: "var(--lt-blue-bright)" }}>*</span>
              </label>
              <textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                required
                rows={6}
                placeholder="Briefly describe your site, asset or requirement..."
                className="field w-full rounded-md px-4 py-3 text-sm resize-y"
                data-testid="contact-field-message"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-xs text-[color:var(--lt-text-mute)] max-w-md">
                We respect your privacy — your details are used only to respond to
                this enquiry.
              </p>
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold disabled:opacity-70"
                data-testid="contact-submit"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Enquiry <ArrowRight size={15} />
                  </>
                )}
              </button>
            </div>

            {status === "success" && (
              <div
                className="rounded-md p-4 flex items-start gap-3"
                style={{
                  background: "rgba(47, 107, 255, 0.10)",
                  border: "1px solid rgba(127, 166, 255, 0.45)",
                }}
                data-testid="contact-success"
              >
                <CheckCircle2 size={18} color="var(--lt-blue-bright)" className="mt-0.5" />
                <p className="text-sm text-white">
                  Thanks — your enquiry has been received. A Lutrom engineer will
                  be in touch shortly.
                </p>
              </div>
            )}
            {status === "error" && (
              <div
                className="rounded-md p-4 flex items-start gap-3"
                style={{
                  background: "rgba(255, 90, 90, 0.10)",
                  border: "1px solid rgba(255, 120, 120, 0.45)",
                }}
                data-testid="contact-error"
              >
                <AlertCircle size={18} color="#ff8d8d" className="mt-0.5" />
                <p className="text-sm text-white">{error}</p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Response times, coverage & FAQ — boosts topical depth and word count for SEO */}
      <section
        className="relative overflow-hidden pb-24 pt-20 mt-20"
        data-testid="contact-info-blocks"
      >
        <PhotoBackdrop src="/gallery/pulley-halves.jpg" position="center 50%" dim={1.08} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          <div className="glass-card rounded-xl p-6" data-testid="contact-hours-card">
            <p className="eyebrow">Coverage</p>
            <h3 className="font-display text-white mt-3 text-2xl leading-none">
              24 / 7 CALL-OUT
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
              Emergency mechanical or electrical breakdown? Our on-call
              engineers respond around the clock, seven days a week — including
              weekends and bank holidays. Typical arrival window across
              Cheshire, Merseyside and Greater Manchester is under two hours.
            </p>
          </div>

          <div className="glass-card rounded-xl p-6" data-testid="contact-office-card">
            <p className="eyebrow">Office hours</p>
            <h3 className="font-display text-white mt-3 text-2xl leading-none">
              MON – FRI · 08:00 – 17:00
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
              Non-urgent quotes, PPM scheduling, EICR bookings and
              account-managed work are handled in-office during standard
              working hours. Enquiries submitted through this form outside
              office hours are picked up the next working day — unless flagged
              as urgent, in which case they hit the on-call engineer directly.
            </p>
          </div>

          <div className="glass-card rounded-xl p-6" data-testid="contact-response-card">
            <p className="eyebrow">Response promise</p>
            <h3 className="font-display text-white mt-3 text-2xl leading-none">
              ONE ENGINEER-LED CONTACT
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
              Every enquiry is triaged by a working engineer — not a call
              centre. You'll get a call or reply within one business hour on
              weekdays, and inside 30 minutes for out-of-hours emergencies.
              Where a site visit is needed we'll agree a slot on the same call.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">Enquiry FAQ</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 0.95 }}
            >
              QUESTIONS BEFORE
              <br />
              <span className="gradient-blue-text">YOU GET IN TOUCH.</span>
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
              A few common ones we hear from facilities managers, procurement
              teams and site engineers. If yours isn't covered here, ring the
              24/7 line and speak to an engineer directly.
            </p>
          </div>

          <div className="lg:col-span-8 space-y-5" data-testid="contact-faq-list">
            {[
              {
                q: "How quickly can Lutrom mobilise to my site?",
                a: "For emergency call-outs across the North West we aim for under two hours on-site. Non-urgent surveys, EICRs and PPMs are typically booked in within five to ten working days depending on scope. We service Cheshire, Merseyside, Greater Manchester, Lancashire and by arrangement across the wider UK.",
              },
              {
                q: "What information should I include in my enquiry?",
                a: "The more we know upfront, the faster we can quote. Useful details: site address and access constraints, asset make/model or DB reference where relevant, the fault or work required, any statutory deadlines (EICR expiry, insurance dates), and whether the work is reactive, planned or a bespoke build. Photos or a schedule attachment help too — we're happy to receive them by email at enquiries@lutrom.co.uk after the form submit.",
              },
              {
                q: "Are you insured and independently certified?",
                a: "Yes. Lutrom is externally audited to ISO 9001 (quality), ISO 14001 (environment) and ISO 45001 (health & safety), and holds current SafeContractor, IPAF and City & Guilds accreditations. Employer's and public liability insurance certificates are provided on request or via our operations portal to approved clients.",
              },
              {
                q: "Do you take on both one-off jobs and long-term contracts?",
                a: "Both. Roughly half our workload is reactive maintenance and bespoke machinery builds; the other half is planned preventative maintenance (PPM), EICR programmes and multi-site rolling contracts for manufacturing, food & beverage, pharmaceutical, logistics and retail clients.",
              },
              {
                q: "Can you handle mechanical, electrical and controls under one contract?",
                a: "That's exactly the point. Lutrom is a single-supplier for mechanical, electrical, hydraulics, pneumatics, water systems, welding, fabrication, PLC/HMI programming, fire alarms, emergency lighting and bespoke machinery — so you get one accountable team, one paper trail and one invoice per job. No subcontractor merry-go-round.",
              },
              {
                q: "How does Lutrom price its work?",
                a: "For one-off work — like machine servicing, refurbishment, bespoke machinery builds, electrical installation and testing — we provide a written quote on request, itemised to the scope. For ongoing long-term contracts we work on an agreed hourly rate. For emergency call-outs we charge a call-out fee that covers travel plus the first hour on site; any additional time is billed at our standard hourly rate. Whichever route suits your operation, we always walk through terms and expectations with new clients before any work commences.",
              },
            ].map((item, i) => (
              <details
                key={i}
                className="glass-card rounded-xl px-5 py-4 group"
                data-testid={`contact-faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="text-white font-semibold text-[15px]">
                    {item.q}
                  </span>
                  <span
                    className="text-[color:var(--lt-blue-bright)] text-xl leading-none transition-transform group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, type = "text", testid, value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-[color:var(--lt-text-dim)] mb-2">
      {label}
      {required && <span style={{ color: "var(--lt-blue-bright)" }}> *</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="field w-full rounded-md px-4 py-3 text-sm"
      data-testid={testid}
    />
  </div>
);

export default Contact;
